import type { EPCR, VitalSet, Procedure, MedicationGiven } from '@/types';

/**
 * Transfer-of-Care script builder — addendum § 10.
 *
 * Generates a structured MIST+ (Mechanism · Injuries/Illness · Signs ·
 * Treatment · Pertinent negatives · Trending) bullet script from the
 * ePCR's current data. The crew reads it aloud to the receiving
 * clinician; the receiving clinician gets a clean, consistent handoff
 * every time; missing data surfaces as '[not documented]' visible
 * placeholders which creates natural pressure toward complete
 * upstream documentation.
 *
 * Three variants:
 *   - buildTOCScript       standard medical / trauma TOC
 *   - buildCode99TOCScript resuscitated cardiac arrest variant
 *                          (down time, shocks, meds, ROSC timing)
 *   - buildRefusalTOCScript refusal-of-care / refusal-of-transport
 *                          handoff (to PCP, family, LEO)
 *
 * Each returns an ordered list of { n, category, text } — numbered
 * bullets ready for display in a 24pt-minimum read-aloud view.
 */

export interface TOCBullet {
  n: number;
  category: string;
  text: string;
  /** True if the bullet has any unknown/missing data — renders '[not documented]' visibly. */
  hasGaps: boolean;
}

interface Context {
  epcr: EPCR;
  vitals: VitalSet[];
  procedures: Procedure[];
  medications: MedicationGiven[];
}

// ------------------------------------------------------------------
// Standard MIST+ (medical / trauma)
// ------------------------------------------------------------------
export function buildTOCScript(ctx: Context): TOCBullet[] {
  const { epcr, vitals, procedures, medications } = ctx;
  const out: TOCBullet[] = [];

  // 1. Patient Identifier — name + age + sex
  const name = [epcr.patient_first_name, epcr.patient_last_name].filter(Boolean).join(' ') || '[name not documented]';
  const age = ageString(epcr.patient_dob);
  const sex = epcr.patient_gender || '[sex not documented]';
  out.push({
    n: 1,
    category: 'Patient Identifier',
    text: `${name}, ${age} ${sex.toLowerCase()}.`,
    hasGaps: !epcr.patient_first_name || !epcr.patient_last_name || !epcr.patient_dob || !epcr.patient_gender,
  });

  // 2. Mechanism / Chief Complaint
  const isTrauma = /trauma|mvc|fall|gunshot|stab|assault|burn/i.test(
    `${epcr.primary_impression ?? ''} ${(epcr.secondary_impressions ?? []).join(' ')}`,
  );
  const mechanism = isTrauma
    ? (epcr.primary_impression
        ? `Mechanism: ${epcr.primary_impression.toLowerCase()}${
          epcr.chief_complaint ? `, with chief complaint of ${epcr.chief_complaint.toLowerCase()}` : ''
        }.`
        : '[mechanism not documented]')
    : (epcr.chief_complaint
        ? `Chief complaint: ${epcr.chief_complaint.toLowerCase()}${
          epcr.primary_impression ? `, working impression ${epcr.primary_impression.toLowerCase()}` : ''
        }.`
        : '[chief complaint not documented]');
  out.push({
    n: 2,
    category: isTrauma ? 'Mechanism' : 'Chief Complaint',
    text: mechanism,
    hasGaps: !epcr.chief_complaint && !epcr.primary_impression,
  });

  // 3. Pertinent History — filtered to what matters for the presentation
  const histParts: string[] = [];
  if ((epcr.medical_history ?? []).length > 0) {
    histParts.push(`History of ${epcr.medical_history.join(', ').toLowerCase()}`);
  }
  if ((epcr.current_medications ?? []).length > 0) {
    const meds = epcr.current_medications.slice(0, 4).map((m) => m.name).join(', ');
    histParts.push(`takes ${meds}${epcr.current_medications.length > 4 ? ' and others' : ''}`);
  }
  const allergyLabel = allergyString(epcr.allergies ?? []);
  histParts.push(allergyLabel);
  out.push({
    n: 3,
    category: 'Pertinent History',
    text: histParts.length ? `${histParts.join('. ')}.` : '[history not documented]',
    hasGaps: histParts.length === 0,
  });

  // 4. Assessment Findings — signs + symptoms from the assessment tabs
  const findings: string[] = [];
  if ((epcr.signs ?? []).length > 0) findings.push((epcr.signs ?? []).join(', ').toLowerCase());
  if ((epcr.symptoms ?? []).length > 0) findings.push(`reporting ${(epcr.symptoms ?? []).join(', ').toLowerCase()}`);
  out.push({
    n: 4,
    category: 'Assessment Findings',
    text: findings.length
      ? `Patient presenting ${findings.join(', ')}.`
      : '[assessment findings not documented]',
    hasGaps: findings.length === 0,
  });

  // 5. Vitals (current + trend)
  const lastVitals = vitals.slice().sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''))[0];
  const firstVitals = vitals.slice().sort((a, b) => (a.timestamp ?? '').localeCompare(b.timestamp ?? ''))[0];
  out.push({
    n: 5,
    category: 'Vitals (Current & Trend)',
    text: lastVitals
      ? vitalsLine(lastVitals, firstVitals !== lastVitals ? firstVitals : null)
      : '[no vital sets documented]',
    hasGaps: !lastVitals,
  });

  // 6. Treatment Given + Response
  const treatmentParts: string[] = [];
  for (const m of medications) {
    const dose = [m.dose, m.unit].filter(Boolean).join(' ');
    treatmentParts.push(`${m.drug_name} ${dose}${m.route ? ` ${m.route}` : ''}`.trim());
  }
  for (const p of procedures) {
    treatmentParts.push(p.procedure_type + (p.outcome === 'successful' ? '' : ` (${p.outcome})`));
  }
  out.push({
    n: 6,
    category: 'Treatment & Response',
    text: treatmentParts.length
      ? `Administered ${treatmentParts.join(', ')}. Patient response: ${lastVitals?.pain_scale !== null && lastVitals?.pain_scale !== undefined ? `pain now ${lastVitals.pain_scale}/10` : 'see vitals trend above'}.`
      : '[no interventions documented]',
    hasGaps: treatmentParts.length === 0,
  });

  return out;
}

// ------------------------------------------------------------------
// Code 99 variant
// ------------------------------------------------------------------
export function buildCode99TOCScript(ctx: Context & {
  arrestTime?: string | null;
  cprStartedAt?: string | null;
  roscTime?: string | null;
  totalShocks?: number;
  totalEpiDoses?: number;
  airwaySummary?: string | null;
}): TOCBullet[] {
  const { epcr, vitals, arrestTime, cprStartedAt, roscTime, totalShocks, totalEpiDoses, airwaySummary } = ctx;
  const out: TOCBullet[] = [];

  const name = [epcr.patient_first_name, epcr.patient_last_name].filter(Boolean).join(' ') || '[name not documented]';
  out.push({
    n: 1,
    category: 'Patient Identifier',
    text: `${name}, ${ageString(epcr.patient_dob)} ${(epcr.patient_gender ?? '?').toLowerCase()}.`,
    hasGaps: !epcr.patient_first_name || !epcr.patient_dob,
  });

  // Down time + bystander CPR + initial rhythm
  const downMinutes = arrestTime && cprStartedAt
    ? Math.max(0, Math.round((new Date(cprStartedAt).getTime() - new Date(arrestTime).getTime()) / 60000))
    : null;
  out.push({
    n: 2,
    category: 'Down Time & Initial Rhythm',
    text: downMinutes !== null
      ? `Estimated down time ${downMinutes} minutes before CPR initiated at ${timeOf(cprStartedAt)}.`
      : `[down time not documented]`,
    hasGaps: downMinutes === null,
  });

  // Totals
  out.push({
    n: 3,
    category: 'Resuscitation Summary',
    text: `${totalShocks ?? 0} defibrillation${(totalShocks ?? 0) === 1 ? '' : 's'}, ${totalEpiDoses ?? 0} epinephrine dose${(totalEpiDoses ?? 0) === 1 ? '' : 's'} administered.`,
    hasGaps: (totalShocks ?? 0) === 0 && (totalEpiDoses ?? 0) === 0,
  });

  // Airway
  out.push({
    n: 4,
    category: 'Airway',
    text: airwaySummary ?? '[airway not documented]',
    hasGaps: !airwaySummary,
  });

  // ROSC
  out.push({
    n: 5,
    category: 'ROSC / Outcome',
    text: roscTime
      ? `Return of spontaneous circulation at ${timeOf(roscTime)}.`
      : 'ROSC not achieved at time of arrival.',
    hasGaps: false,
  });

  // Post-ROSC 12-lead + vitals
  const lastVitals = vitals.slice().sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''))[0];
  out.push({
    n: 6,
    category: 'Current Status',
    text: lastVitals
      ? vitalsLine(lastVitals, null)
      : '[no post-ROSC vital set documented]',
    hasGaps: !lastVitals,
  });

  return out;
}

// ------------------------------------------------------------------
// Refusal variant
// ------------------------------------------------------------------
export function buildRefusalTOCScript(ctx: Context): TOCBullet[] {
  const { epcr, vitals } = ctx;
  const out: TOCBullet[] = [];
  const name = [epcr.patient_first_name, epcr.patient_last_name].filter(Boolean).join(' ') || '[name not documented]';
  const lastVitals = vitals.slice().sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''))[0];

  out.push({
    n: 1,
    category: 'Patient Identifier',
    text: `${name}, ${ageString(epcr.patient_dob)} ${(epcr.patient_gender ?? '?').toLowerCase()}.`,
    hasGaps: !epcr.patient_first_name,
  });

  out.push({
    n: 2,
    category: 'Chief Complaint & Findings',
    text: epcr.chief_complaint ? `Chief complaint: ${epcr.chief_complaint.toLowerCase()}.` : '[chief complaint not documented]',
    hasGaps: !epcr.chief_complaint,
  });

  out.push({
    n: 3,
    category: 'Capacity & Findings',
    text: lastVitals
      ? `${vitalsLine(lastVitals, null)} Patient alert and able to make informed decisions.`
      : 'Patient alert and able to make informed decisions. [vitals not documented]',
    hasGaps: !lastVitals,
  });

  out.push({
    n: 4,
    category: 'Treatment Offered & Risks Explained',
    text: '[document in narrative: treatment offered, risks of refusal explained, understanding confirmed]',
    hasGaps: true,
  });

  out.push({
    n: 5,
    category: 'Patient Decision',
    text: 'Patient refused care / transport against medical advice after risks were explained.',
    hasGaps: false,
  });

  out.push({
    n: 6,
    category: 'Follow-Up Recommendations',
    text: 'Recommended: self-transport to ED if symptoms change, PCP follow-up within 24 hours, call 911 immediately for worsening symptoms.',
    hasGaps: false,
  });

  return out;
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
function ageString(dob: string | null): string {
  if (!dob) return '[age not documented]';
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return '[age not documented]';
  const years = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  return `${years}-year-old`;
}

function timeOf(iso: string | null | undefined): string {
  if (!iso) return '[time unknown]';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '[time unknown]';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function allergyString(allergies: EPCR['allergies']): string {
  if (!allergies || allergies.length === 0) return '[allergies not documented]';
  const hasNKA = allergies.some((a) => a.agent === 'NKA');
  const hasNKDA = allergies.some((a) => a.agent === 'NKDA');
  const specific = allergies.filter((a) => a.agent !== 'NKA' && a.agent !== 'NKDA');
  if (specific.length === 0) {
    if (hasNKA && hasNKDA) return 'NKA / NKDA';
    if (hasNKDA) return 'NKDA';
    if (hasNKA) return 'NKA';
    return '[allergies not documented]';
  }
  return `Allergic to ${specific.map((a) => a.agent).join(', ').toLowerCase()}`;
}

function vitalsLine(v: VitalSet, initial: VitalSet | null): string {
  const parts: string[] = [];
  if (v.bp_systolic != null && v.bp_diastolic != null) {
    parts.push(`BP ${v.bp_systolic}/${v.bp_diastolic}`);
  }
  if (v.heart_rate != null) parts.push(`HR ${v.heart_rate}${v.heart_rhythm ? ` ${v.heart_rhythm}` : ''}`);
  if (v.respiratory_rate != null) parts.push(`RR ${v.respiratory_rate}`);
  if (v.spo2 != null) parts.push(`SpO2 ${v.spo2}%`);
  if (v.gcs_total != null) parts.push(`GCS ${v.gcs_total}`);
  let line = parts.length ? `Current: ${parts.join(', ')}.` : '[no vital elements captured]';
  if (initial) {
    const change: string[] = [];
    if (initial.bp_systolic != null && v.bp_systolic != null && Math.abs(initial.bp_systolic - v.bp_systolic) >= 10) {
      change.push(`SBP ${initial.bp_systolic} → ${v.bp_systolic}`);
    }
    if (initial.heart_rate != null && v.heart_rate != null && Math.abs(initial.heart_rate - v.heart_rate) >= 10) {
      change.push(`HR ${initial.heart_rate} → ${v.heart_rate}`);
    }
    if (change.length) line += ` Trend: ${change.join(', ')}.`;
  }
  return line;
}
